import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatDateJP,
  toDateInput,
  toTimeInput,
} from '../../../utils/datetime';
import { normalizeCastingProgressSummary } from '../../../utils/castingProgress';
import { getEventCastingSummary } from '../../../api/eventDetails';
import {
  buildParticipantEventMembershipLabel as buildEventParticipantEventMembershipLabel,
} from '../../common/chat/members/eventParticipantUtils';
import {
  buildEventScheduleFromInfo,
  buildGroupEventDetailRows,
  buildHeaderNextMultiPerformance,
  buildHeaderNextPerformance,
  buildUnifiedEventHeaderSchedule,
  resolveHeaderFlyerSource,
} from '../../common/chat/sidepanel/eventSidePanelUtils';
import { normalizeGroupEventRows } from '../../common/chat/eventlist/eventGroupEventUtils';
import {
  EVENT_CHAT_MODE_GROUP_UNIFIED as CHAT_MODE_GROUP_UNIFIED,
  resolveEventChatMode,
  resolveEventGroupCount,
  resolveEventThreadId,
} from '../../common/chat/eventlist/eventThreadMetaUtils';
import { pickFirstTrimmedString as pickFirstString } from '../../common/chat/members/memberListUtils';
import {
  API_URL,
  buildFlyerUrl,
  fetchJsonSafe,
  getCastingProgressStats,
  getTaskProgressStats,
  getTicketProgressStats,
} from './eventChatPageHelpers';

export default function useEventChatSidePanelData({
  events,
  selectedEventId,
  selectedEventInfo,
  sidebarDetails,
  participantSnapshot,
  groupPanelEvents,
  groupEventSidebarRows,
  settingsEvent,
  canViewEventTasks,
  canViewCastingProgress,
  canViewTicketSales,
  groupTaskSummaryMap,
  groupCastingSummaryMap,
  groupTicketSummaryMap,
  ticketSummary,
  taskSummary,
  navigate,
}) {
  const [castingSummary, setCastingSummary] = useState(null);
  useEffect(() => {
    let active = true;
    if (!selectedEventId || !canViewCastingProgress) {
      setCastingSummary(null);
      return undefined;
    }
    getEventCastingSummary(selectedEventId)
      .then((summary) => {
        if (active) setCastingSummary(summary || null);
      })
      .catch(() => {
        if (active) setCastingSummary(null);
      });
    return () => {
      active = false;
    };
  }, [canViewCastingProgress, selectedEventId]);

  const selectedEventAdmins = useMemo(() => {
    if (!selectedEventInfo || !Array.isArray(selectedEventInfo.assigned_admins)) {
      return [];
    }
    const seen = new Set();
    return selectedEventInfo.assigned_admins.filter((admin) => {
      if (!admin) return false;
      const rawKey = admin.admin_user_id ?? admin.id ?? admin.username ?? admin.display_name;
      if (rawKey === null || rawKey === undefined || rawKey === '') return true;
      const key = String(rawKey);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [selectedEventInfo]);

  const isConciergeEvent = useMemo(() => {
    if (!selectedEventInfo) return false;
    const flag = selectedEventInfo.is_concierge_event;
    if (typeof flag === 'boolean') return flag;
    const camelFlag = selectedEventInfo.isConciergeEvent;
    if (typeof camelFlag === 'boolean') return camelFlag;
    return Boolean(
      selectedEventInfo.concierge_application_id ||
      selectedEventInfo.concierge_application
    );
  }, [selectedEventInfo]);

  const conciergePlanner = useMemo(() => {
    if (!isConciergeEvent) return null;
    const app = selectedEventInfo?.concierge_application;
    if (!app) return null;
    const name = pickFirstString(
      app.artist_name,
      app.name,
      app.display_name,
      app.full_name
    ) || '企画者';
    return {
      artist_id: app.artist_id ?? app.id ?? null,
      artist_name: name,
      artist_name_en: app.artist_name_en || app.name_en || '',
      artist_profile_picture: app.artist_profile_picture || app.profile_picture || '',
      artist_parts: app.artist_parts || app.parts || null,
      artist_gender: app.artist_gender || app.gender || null,
      profile_one_liner: app.profile_one_liner || '',
      instagram_url: app.instagram_url || '',
      twitter_url: app.twitter_url || '',
      facebook_url: app.facebook_url || '',
      hp_url: app.hp_url || '',
    };
  }, [selectedEventInfo, isConciergeEvent]);

  const adminLookup = useMemo(() => {
    const map = new Map();
    selectedEventAdmins.forEach((admin) => {
      if (!admin) return;
      const id = admin.admin_user_id ?? admin.id ?? admin.user_id ?? admin.username;
      if (id == null) return;
      map.set(String(id), admin);
    });
    return map;
  }, [selectedEventAdmins]);

  const participantGroupEvents = useMemo(() => {
    const source = Array.isArray(participantSnapshot?.group_events)
      ? participantSnapshot.group_events
      : [];
    const normalized = source
      .map((event) => ({
        event_uuid: event?.event_uuid ? String(event.event_uuid) : '',
        name: pickFirstString(event?.name),
        performance_type: pickFirstString(event?.performance_type),
        event_date: event?.event_date || null,
        open_time: event?.open_time || null,
        start_time: event?.start_time || null,
        venue_name: pickFirstString(event?.venue_name, event?.venue, event?.place),
        venue: pickFirstString(event?.venue, event?.place),
        prefecture_name: pickFirstString(event?.prefecture_name, event?.prefecture),
        city_name: pickFirstString(event?.city_name, event?.city),
        venue_address: pickFirstString(event?.venue_address, event?.address),
        memo: pickFirstString(event?.memo, event?.note, event?.remarks),
        is_primary: event?.is_primary === true,
      }))
      .filter((event) => event.event_uuid);
    if (normalized.length > 0) return normalized;
    if (!selectedEventId && !selectedEventInfo?.event_uuid) return [];
    return [{
      event_uuid: String(selectedEventInfo?.event_uuid || selectedEventId),
      name: pickFirstString(selectedEventInfo?.name),
      performance_type: pickFirstString(selectedEventInfo?.performance_type),
      event_date: selectedEventInfo?.event_date || null,
      open_time: selectedEventInfo?.open_time || null,
      start_time: selectedEventInfo?.start_time || null,
      venue_name: pickFirstString(selectedEventInfo?.venue_name, selectedEventInfo?.venue, selectedEventInfo?.place),
      venue: pickFirstString(selectedEventInfo?.venue, selectedEventInfo?.place),
      prefecture_name: pickFirstString(selectedEventInfo?.prefecture_name, selectedEventInfo?.prefecture),
      city_name: pickFirstString(selectedEventInfo?.city_name, selectedEventInfo?.city),
      venue_address: pickFirstString(selectedEventInfo?.venue_address, selectedEventInfo?.address),
      memo: pickFirstString(selectedEventInfo?.memo, selectedEventInfo?.note, selectedEventInfo?.remarks),
      is_primary: true,
    }];
  }, [participantSnapshot, selectedEventInfo, selectedEventId]);

  const selectedEventListRow = useMemo(() => (
    Array.isArray(events)
      ? events.find((evt) => String(evt?.event_uuid || '') === String(selectedEventId || '')) || null
      : null
  ), [events, selectedEventId]);

  const isSelectedGroupUnified = useMemo(() => {
    const selectedThreadId = resolveEventThreadId(
      selectedEventListRow || selectedEventInfo || sidebarDetails || participantSnapshot || {}
    );
    const sameThreadEventCount = selectedThreadId
      ? (Array.isArray(events)
          ? events.filter((evt) => resolveEventThreadId(evt) === selectedThreadId).length
          : 0)
      : 0;
    const chatModes = [
      resolveEventChatMode(selectedEventListRow || {}),
      resolveEventChatMode(selectedEventInfo || {}),
      resolveEventChatMode(sidebarDetails || {}),
      resolveEventChatMode(participantSnapshot || {}),
    ];
    if (chatModes.includes(CHAT_MODE_GROUP_UNIFIED)) return true;

    const groupCounts = [
      resolveEventGroupCount(selectedEventListRow || {}),
      resolveEventGroupCount(selectedEventInfo || {}),
      resolveEventGroupCount(sidebarDetails || {}),
      resolveEventGroupCount(participantSnapshot || {}),
      Number(participantGroupEvents.length || 0),
      Number(sameThreadEventCount || 0),
    ];
    return groupCounts.some((count) => Number(count) > 1);
  }, [events, selectedEventInfo, selectedEventListRow, sidebarDetails, participantSnapshot, participantGroupEvents]);

  const isSettingsGroupUnified = useMemo(() => {
    const target = settingsEvent || selectedEventInfo || {};
    if (resolveEventChatMode(target) === CHAT_MODE_GROUP_UNIFIED) return true;
    return resolveEventGroupCount(target) > 1;
  }, [selectedEventInfo, settingsEvent]);

  const effectiveGroupEvents = useMemo(() => {
    if (!isSelectedGroupUnified) return participantGroupEvents;
    const normalizedPanel = normalizeGroupEventRows(groupPanelEvents);
    if (normalizedPanel.length > 0) return normalizedPanel;
    return participantGroupEvents;
  }, [groupPanelEvents, isSelectedGroupUnified, participantGroupEvents]);

  const groupEventDetailRows = useMemo(() => (
    buildGroupEventDetailRows({
      isSelectedGroupUnified,
      effectiveGroupEvents,
      groupEventSidebarRows,
      pickFirstString,
      toDateInput,
      formatDateJP,
      toTimeInput,
      getExtraRowFields: ({ eventUuid }) => ({
        taskProgress: canViewEventTasks
          ? getTaskProgressStats(groupTaskSummaryMap?.[eventUuid])
          : null,
        castingProgress: canViewCastingProgress
          ? getCastingProgressStats(groupCastingSummaryMap?.[eventUuid])
          : null,
        ticketProgress: canViewTicketSales
          ? getTicketProgressStats(groupTicketSummaryMap?.[eventUuid])
          : null,
      }),
    })
  ), [
    canViewCastingProgress,
    canViewEventTasks,
    canViewTicketSales,
    effectiveGroupEvents,
    groupCastingSummaryMap,
    groupEventSidebarRows,
    groupTaskSummaryMap,
    groupTicketSummaryMap,
    isSelectedGroupUnified,
  ]);

  const participantGroupEventOrderMap = useMemo(() => {
    const map = new Map();
    effectiveGroupEvents.forEach((event, index) => {
      if (!event?.event_uuid) return;
      map.set(String(event.event_uuid), index);
    });
    return map;
  }, [effectiveGroupEvents]);

  const participantGroupEventMap = useMemo(() => {
    const map = new Map();
    effectiveGroupEvents.forEach((event) => {
      if (!event?.event_uuid) return;
      map.set(String(event.event_uuid), event);
    });
    return map;
  }, [effectiveGroupEvents]);

  const buildParticipantEventMembershipLabel = useCallback((eventUuids) => (
    buildEventParticipantEventMembershipLabel({
      eventUuids,
      targetGroupEvents: effectiveGroupEvents,
      participantGroupEventOrderMap,
      participantGroupEventMap,
      pickFirstString,
    })
  ), [effectiveGroupEvents, participantGroupEventMap, participantGroupEventOrderMap]);

  const eventSchedule = useMemo(() => (
    buildEventScheduleFromInfo({
      selectedEventInfo,
      toDateInput,
      formatDateJP,
      toTimeInput,
      includeLabel: true,
    })
  ), [selectedEventInfo]);

  const eventHeaderSchedule = useMemo(() => (
    buildUnifiedEventHeaderSchedule({
      isSelectedGroupUnified,
      eventSchedule,
      groupEventSidebarRows,
      effectiveGroupEvents,
      toDateInput,
      formatDateJP,
    })
  ), [effectiveGroupEvents, eventSchedule, groupEventSidebarRows, isSelectedGroupUnified]);

  const headerPerformanceType = useMemo(() => {
    if (isSelectedGroupUnified) return '';
    return pickFirstString(
      selectedEventListRow?.performance_type,
      sidebarDetails?.performance_type,
      selectedEventInfo?.performance_type,
      participantGroupEvents[0]?.performance_type
    );
  }, [isSelectedGroupUnified, participantGroupEvents, selectedEventInfo, selectedEventListRow, sidebarDetails]);

  const headerNextPerformance = useMemo(() => (
    buildHeaderNextPerformance({
      isSelectedGroupUnified,
      groupEventDetailRows,
    })
  ), [groupEventDetailRows, isSelectedGroupUnified]);

  const isSelectedMultiPerformanceEvent = !isSelectedGroupUnified &&
    selectedEventInfo?.event_mode === 'multi_performance' &&
    Array.isArray(selectedEventInfo?.performances) &&
    selectedEventInfo.performances.length > 0;

  const headerNextMultiPerformance = useMemo(() => (
    buildHeaderNextMultiPerformance({
      isMultiPerformanceEvent: isSelectedMultiPerformanceEvent,
      performances: selectedEventInfo?.performances,
    })
  ), [isSelectedMultiPerformanceEvent, selectedEventInfo?.performances]);

  const participantCounts = useMemo(() => {
    const parse = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };
    const fromSnapshot = participantSnapshot?.participant_counts ?? {};
    const snapshotArtists = parse(fromSnapshot.artists);
    const snapshotAdmins = parse(fromSnapshot.admins);
    const snapshotConcierge = parse(fromSnapshot.concierge);
    const snapshotTotal = parse(fromSnapshot.total);
    if (
      snapshotArtists !== null ||
      snapshotAdmins !== null ||
      snapshotConcierge !== null ||
      snapshotTotal !== null
    ) {
      const artists = snapshotArtists ?? 0;
      const admins = snapshotAdmins ?? 0;
      const concierge = snapshotConcierge ?? 0;
      const total = snapshotTotal ?? (artists + admins + concierge);
      return { artists, admins, concierge, total };
    }

    const fromApi = selectedEventInfo?.participant_counts ?? {};
    const artists = parse(fromApi.artists) ?? 0;
    const admins = selectedEventAdmins.length;
    const concierge = isConciergeEvent && conciergePlanner ? 1 : 0;
    const totalFromApi = parse(fromApi.total);
    const totalBase = totalFromApi ?? (artists + admins + concierge);
    const total = isConciergeEvent ? totalBase : (artists + admins);
    return { artists, admins, concierge, total };
  }, [participantSnapshot, selectedEventInfo, selectedEventAdmins, conciergePlanner, isConciergeEvent]);

  const hasConciergeParticipants = useMemo(() => {
    if (Number(participantCounts.concierge || 0) > 0) return true;
    const snapshotList = Array.isArray(participantSnapshot?.concierge_planners)
      ? participantSnapshot.concierge_planners
      : [];
    if (snapshotList.length > 0) return true;
    return isConciergeEvent && !!conciergePlanner;
  }, [participantCounts.concierge, participantSnapshot, isConciergeEvent, conciergePlanner]);

  const castingProgressStats = useMemo(
    () => normalizeCastingProgressSummary(castingSummary),
    [castingSummary]
  );
  const castingPct = castingProgressStats.percent || 0;

  const ticketProgress = useMemo(() => {
    const sold = Number(ticketSummary?.sold_total ?? ticketSummary?.soldTotal ?? 0) || 0;
    const capacity = Number(ticketSummary?.total_capacity ?? ticketSummary?.totalCapacity ?? 0) || 0;
    const pctFromSummary = Number(ticketSummary?.progress_pct ?? ticketSummary?.progressPct);
    const pct =
      capacity > 0
        ? Math.min(100, Math.max(0, Math.round((sold / capacity) * 100)))
        : Number.isFinite(pctFromSummary) ? pctFromSummary : 0;
    return {
      pct: Math.min(100, Math.max(0, Math.round(pct || 0))),
      sold,
      capacity,
    };
  }, [ticketSummary]);

  const taskProgress = useMemo(() => {
    const stats = getTaskProgressStats(taskSummary);
    return stats || { percent: 0 };
  }, [taskSummary]);

  const scheduleDetail = useMemo(() => {
    const open = toTimeInput(sidebarDetails?.open_time || selectedEventInfo?.open_time) || '';
    const start = toTimeInput(sidebarDetails?.start_time || selectedEventInfo?.start_time) || '';
    return { open, start };
  }, [sidebarDetails, selectedEventInfo]);

  const venueInfo = useMemo(() => {
    const name = pickFirstString(
      sidebarDetails?.venue_name,
      sidebarDetails?.venue,
      sidebarDetails?.place,
      selectedEventInfo?.venue,
      selectedEventInfo?.venue_name
    ) || '会場未設定';
    const pref = pickFirstString(sidebarDetails?.prefecture_name, sidebarDetails?.prefecture);
    const city = pickFirstString(sidebarDetails?.city, sidebarDetails?.city_name);
    const addressLine = pickFirstString(sidebarDetails?.address, sidebarDetails?.venue_address);
    const address = [pref, city, addressLine].filter(Boolean).join(' ');
    return { name, address };
  }, [sidebarDetails, selectedEventInfo]);

  const sidebarNote = useMemo(() => (
    pickFirstString(sidebarDetails?.note, sidebarDetails?.remarks, sidebarDetails?.memo)
  ), [sidebarDetails]);

  const headerFlyerSource = useMemo(() => (
    resolveHeaderFlyerSource({
      isSelectedGroupUnified,
      headerNextPerformanceEventUuid: headerNextPerformance?.eventUuid,
      groupEventSidebarRows,
      effectiveGroupEvents,
    })
  ), [effectiveGroupEvents, groupEventSidebarRows, headerNextPerformance?.eventUuid, isSelectedGroupUnified]);

  const flyerFrontUrl = selectedEventInfo?.flyer_front_url || '';
  const flyerBackUrl = selectedEventInfo?.flyer_back_url || '';
  const selectedEventFlyerUrl = useMemo(() => {
    const key = pickFirstString(
      headerFlyerSource?.flyer_front_url,
      sidebarDetails?.flyer_front_url,
      flyerFrontUrl
    );
    if (!key) return '';
    return buildFlyerUrl(key, 'card');
  }, [flyerFrontUrl, headerFlyerSource, sidebarDetails]);

  const selectedEventFlyerBackUrl = useMemo(() => {
    const key = pickFirstString(
      headerFlyerSource?.flyer_back_url,
      sidebarDetails?.flyer_back_url,
      flyerBackUrl
    );
    if (!key) return '';
    return buildFlyerUrl(key, 'card');
  }, [flyerBackUrl, headerFlyerSource, sidebarDetails]);

  const selectedEventFlyerFullUrl = useMemo(() => {
    const key = pickFirstString(
      headerFlyerSource?.flyer_front_url,
      sidebarDetails?.flyer_front_url,
      flyerFrontUrl
    );
    if (!key) return '';
    return buildFlyerUrl(key, null);
  }, [flyerFrontUrl, headerFlyerSource, sidebarDetails]);

  const selectedEventFlyerBackFullUrl = useMemo(() => {
    const key = pickFirstString(
      headerFlyerSource?.flyer_back_url,
      sidebarDetails?.flyer_back_url,
      flyerBackUrl
    );
    if (!key) return '';
    return buildFlyerUrl(key, null);
  }, [flyerBackUrl, headerFlyerSource, sidebarDetails]);

  const headerDetailEventId = useMemo(() => {
    if (!isSelectedGroupUnified) return selectedEventId;
    return headerNextPerformance?.eventUuid || selectedEventId;
  }, [headerNextPerformance?.eventUuid, isSelectedGroupUnified, selectedEventId]);

  const handleGoHeaderEventDetail = useCallback(() => {
    if (!headerDetailEventId) return;
    navigate(`/admin/workspaces/event-operations/events/${headerDetailEventId}`);
  }, [headerDetailEventId, navigate]);

  const eventDetailUrl = selectedEventId ? `/admin/workspaces/event-operations/events/${selectedEventId}` : '';

  const resolveVenueLink = useCallback(async (venueId) => {
    if (!venueId) return { link: null, address: '', name: '' };
    try {
      const detail = await fetchJsonSafe(`${API_URL}/api/venues/${venueId}`, {}, { label: '' });
      const venue = detail?.venue || detail || {};
      const address = [venue.prefecture, venue.city, venue.address].filter(Boolean).join(' ').trim();
      return { link: `/admin/workspaces/event-operations/venues/${venueId}`, address, name: venue?.name || '' };
    } catch {
      return { link: `/admin/workspaces/event-operations/venues/${venueId}`, address: '', name: '' };
    }
  }, []);

  const venueIdFromData =
    sidebarDetails?.venue_id ??
    sidebarDetails?.venue?.id ??
    sidebarDetails?.venue?.venue_id ??
    selectedEventInfo?.venue_id ??
    selectedEventInfo?.venueId ??
    selectedEventInfo?.venue?.id ??
    null;

  const [venueLink, setVenueLink] = useState(null);
  const [venueResolvedAddress, setVenueResolvedAddress] = useState('');
  const [venueResolvedName, setVenueResolvedName] = useState('');

  const mapLinkUrl = useMemo(() => {
    const q = venueResolvedName || venueInfo.name || venueResolvedAddress || venueInfo.address || '';
    return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : '';
  }, [venueInfo, venueResolvedAddress, venueResolvedName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { link, address, name } = await resolveVenueLink(venueIdFromData);
      if (cancelled) return;
      setVenueLink(link);
      setVenueResolvedAddress(address || '');
      setVenueResolvedName((name || '').trim());
    })();
    return () => { cancelled = true; };
  }, [resolveVenueLink, venueIdFromData]);

  return {
    selectedEventAdmins,
    isConciergeEvent,
    conciergePlanner,
    adminLookup,
    participantGroupEvents,
    selectedEventListRow,
    isSelectedGroupUnified,
    isSettingsGroupUnified,
    effectiveGroupEvents,
    groupEventDetailRows,
    participantGroupEventOrderMap,
    participantGroupEventMap,
    buildParticipantEventMembershipLabel,
    eventSchedule,
    eventHeaderSchedule,
    headerPerformanceType,
    headerNextPerformance,
    isSelectedMultiPerformanceEvent,
    headerNextMultiPerformance,
    participantCounts,
    hasConciergeParticipants,
    castingProgressStats,
    castingPct,
    ticketProgress,
    taskProgress,
    scheduleDetail,
    venueInfo,
    sidebarNote,
    selectedEventFlyerUrl,
    selectedEventFlyerBackUrl,
    selectedEventFlyerFullUrl,
    selectedEventFlyerBackFullUrl,
    handleGoHeaderEventDetail,
    eventDetailUrl,
    venueIdFromData,
    venueLink,
    venueResolvedAddress,
    venueResolvedName,
    mapLinkUrl,
  };
}
